import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  ActionIcon,
  Alert,
  Button,
  Center,
  ColorSwatch,
  Container,
  Group,
  Loader,
  MultiSelect,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowDown, IconArrowUp, IconPlus, IconRestore, IconTrash } from '@tabler/icons-react';

import { ChoicePicker, LogoPaper, PageTitle } from '@/components';
import { useJurisdictions, useRiskProfiles } from '@/hooks';
import {
  FINDING_KIND_LABELS,
  FINDING_SEVERITY_LABELS,
  SCREENING_RATING_META,
  SCREENING_RATINGS,
  TAXONOMY_CATEGORIES,
  TAXONOMY_CATEGORY_LABELS,
  type FindingKind,
  type FindingSeverity,
  type RatingRule,
  type RatingRuleMatch,
  type ScreeningRating,
  type TaxonomyCategory,
} from '@/types';

const PLATFORM_DEFAULT = 'platform-default';
const NEW = 'new';

const KIND_OPTIONS = [
  { value: '', label: 'Any kind' },
  ...(Object.keys(FINDING_KIND_LABELS) as FindingKind[]).map((kind) => ({
    value: kind,
    label: FINDING_KIND_LABELS[kind],
  })),
];

const SEVERITY_OPTIONS = (Object.keys(FINDING_SEVERITY_LABELS) as FindingSeverity[]).map((s) => ({
  value: s,
  label: FINDING_SEVERITY_LABELS[s],
}));

const CATEGORY_OPTIONS = TAXONOMY_CATEGORIES.map((c) => ({
  value: c,
  label: TAXONOMY_CATEGORY_LABELS[c],
}));

/** Whether the finding must (not) concern sanctions; the column says so. */
const SANCTIONS_RELATED_OPTIONS = [
  { value: '', label: 'Either' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

/** Whether the SUBJECT must (not) sit in the profile's high-risk list. */
const HRJ_OPTIONS = [
  { value: '', label: 'Any subject' },
  { value: 'true', label: 'High-risk only' },
  { value: 'false', label: 'Non-high-risk only' },
];

const RATING_OPTIONS = SCREENING_RATINGS.map((r) => ({
  value: r,
  label: SCREENING_RATING_META[r].label,
}));

function blankRule(): RatingRule {
  return { match: {}, rating: 'potential', label: '' };
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Drop unset match fields so the stored rule is minimal. */
function cleanRule(rule: RatingRule): RatingRule {
  const match: RatingRuleMatch = {};
  if (rule.match.kind) {
    match.kind = rule.match.kind;
  }
  if (rule.match.categories?.length) {
    match.categories = rule.match.categories;
  }
  if (rule.match.severities?.length) {
    match.severities = rule.match.severities;
  }
  if (typeof rule.match.sanctions_related === 'boolean') {
    match.sanctions_related = rule.match.sanctions_related;
  }
  if (typeof rule.match.min_confidence === 'number') {
    match.min_confidence = rule.match.min_confidence;
  }
  if (typeof rule.match.subject_in_high_risk_jurisdictions === 'boolean') {
    match.subject_in_high_risk_jurisdictions = rule.match.subject_in_high_risk_jurisdictions;
  }
  return { match, rating: rule.rating, label: rule.label?.trim() || undefined };
}

function sanctionsRelatedValue(value: boolean | null | undefined): string {
  if (value === true) {
    return 'true';
  }
  return value === false ? 'false' : '';
}

/** The rating's colour as a dot — the select stays readable at any width. */
function RatingSwatch({ rating }: Readonly<{ rating: ScreeningRating }>) {
  return (
    <ColorSwatch
      size={10}
      color={`var(--mantine-color-${SCREENING_RATING_META[rating].color}-6)`}
      withShadow={false}
    />
  );
}

/** One rule of the ordered table: every match field is optional. Inputs fill
 * their fixed-layout columns; Categories and Label take the remaining width. */
function RuleRow({
  index,
  rule,
  isFirst,
  isLast,
  onChange,
  onMatchChange,
  onMove,
  onRemove,
}: Readonly<{
  index: number;
  rule: RatingRule;
  isFirst: boolean;
  isLast: boolean;
  onChange: (patch: Partial<RatingRule>) => void;
  onMatchChange: (patch: Partial<RatingRuleMatch>) => void;
  onMove: (delta: -1 | 1) => void;
  onRemove: () => void;
}>) {
  const categories = rule.match.categories ?? [];
  const severities = rule.match.severities ?? [];
  return (
    <Table.Tr data-testid={`risk-profile-rule-${index}`}>
      <Table.Td>
        <Text size="sm" c="dimmed" ta="center">
          {index + 1}
        </Text>
      </Table.Td>
      <Table.Td>
        <Select
          aria-label="Kind"
          data={KIND_OPTIONS}
          value={rule.match.kind ?? ''}
          onChange={(v) => onMatchChange({ kind: (v || null) as FindingKind | null })}
          allowDeselect={false}
        />
      </Table.Td>
      <Table.Td>
        {/* Popover pickers, not pill multi-selects: a chosen pill would
            wrap the row, and this table has to stay one line per rule. */}
        <ChoicePicker
          label="Categories"
          options={CATEGORY_OPTIONS}
          value={categories}
          onChange={(v) =>
            onMatchChange({ categories: v.length ? (v as TaxonomyCategory[]) : null })
          }
          searchable
          width={320}
        />
      </Table.Td>
      <Table.Td>
        <ChoicePicker
          label="Severities"
          options={SEVERITY_OPTIONS}
          value={severities}
          onChange={(v) =>
            onMatchChange({ severities: v.length ? (v as FindingSeverity[]) : null })
          }
          width={200}
        />
      </Table.Td>
      <Table.Td>
        <Select
          aria-label="Sanctions-related"
          data={SANCTIONS_RELATED_OPTIONS}
          value={sanctionsRelatedValue(rule.match.sanctions_related)}
          onChange={(v) => onMatchChange({ sanctions_related: v ? v === 'true' : null })}
          allowDeselect={false}
        />
      </Table.Td>
      <Table.Td>
        <Select
          aria-label="High-risk subject"
          data={HRJ_OPTIONS}
          value={sanctionsRelatedValue(rule.match.subject_in_high_risk_jurisdictions)}
          onChange={(v) =>
            onMatchChange({ subject_in_high_risk_jurisdictions: v ? v === 'true' : null })
          }
          allowDeselect={false}
        />
      </Table.Td>
      <Table.Td>
        <NumberInput
          aria-label="Minimum confidence"
          placeholder="any"
          min={0}
          max={1}
          step={0.05}
          decimalScale={2}
          hideControls
          value={rule.match.min_confidence ?? ''}
          onChange={(v) => onMatchChange({ min_confidence: typeof v === 'number' ? v : null })}
        />
      </Table.Td>
      <Table.Td>
        <Select
          aria-label="Rating"
          data={RATING_OPTIONS}
          value={rule.rating}
          onChange={(v) => v && onChange({ rating: v as ScreeningRating })}
          allowDeselect={false}
          leftSection={<RatingSwatch rating={rule.rating} />}
          leftSectionWidth={28}
          renderOption={({ option }) => (
            <Group gap={8} wrap="nowrap">
              <RatingSwatch rating={option.value as ScreeningRating} />
              <span>{option.label}</span>
            </Group>
          )}
        />
      </Table.Td>
      <Table.Td>
        <TextInput
          aria-label="Label"
          placeholder="Why this rule exists"
          value={rule.label ?? ''}
          onChange={(e) => onChange({ label: e.currentTarget.value })}
        />
      </Table.Td>
      <Table.Td>
        <Group gap={2} wrap="nowrap" justify="flex-end">
          <ActionIcon
            variant="subtle"
            aria-label="Move up"
            disabled={isFirst}
            onClick={() => onMove(-1)}
          >
            <IconArrowUp size={16} />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            aria-label="Move down"
            disabled={isLast}
            onClick={() => onMove(1)}
          >
            <IconArrowDown size={16} />
          </ActionIcon>
          <ActionIcon variant="subtle" color="red" aria-label="Remove rule" onClick={onRemove}>
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

/**
 * Create / edit a risk profile, or edit the platform default. Rules are an
 * ordered table: first match wins, and a finding no rule matches rates
 * Potential (never silently Clear).
 */
export function RiskProfileEditPage() {
  const { profileId = NEW } = useParams<{ profileId: string }>();
  const navigate = useNavigate();
  const isNew = profileId === NEW;
  const isPlatform = profileId === PLATFORM_DEFAULT;
  const isNamed = !isNew && !isPlatform;
  const {
    profiles,
    platformDefault,
    isLoading,
    createProfile,
    updateProfile,
    savePlatformDefault,
    loadBuiltinDefault,
  } = useRiskProfiles();

  const existing = useMemo(
    () => (isNamed ? (profiles.find((p) => p.id === profileId) ?? null) : null),
    [isNamed, profiles, profileId]
  );

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [rules, setRules] = useState<RatingRule[]>([]);
  const [highRiskJurisdictions, setHighRiskJurisdictions] = useState<string[]>([]);
  const { jurisdictions } = useJurisdictions();
  const [seeded, setSeeded] = useState(false);
  const [saving, setSaving] = useState(false);

  // Seed the form once the source is known: the profile row, the platform
  // default, or — for a new profile — the platform default's rules as a
  // starting point.
  useEffect(() => {
    if (seeded) {
      return;
    }
    if (isPlatform && platformDefault) {
      setName(platformDefault.name);
      setDescription(platformDefault.description ?? '');
      setRules(platformDefault.rules);
      setHighRiskJurisdictions(platformDefault.highRiskJurisdictions);
      setSeeded(true);
    } else if (isNew && platformDefault) {
      setRules(platformDefault.rules);
      setHighRiskJurisdictions(platformDefault.highRiskJurisdictions);
      setSeeded(true);
    } else if (existing) {
      setName(existing.name);
      setSlug(existing.slug);
      setDescription(existing.description ?? '');
      setRules(existing.rules);
      setHighRiskJurisdictions(existing.highRiskJurisdictions);
      setSeeded(true);
    }
  }, [seeded, isPlatform, isNew, platformDefault, existing]);

  const updateRule = (index: number, patch: Partial<RatingRule>) =>
    setRules((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  const updateMatch = (index: number, patch: Partial<RatingRuleMatch>) =>
    setRules((prev) =>
      prev.map((r, i) => (i === index ? { ...r, match: { ...r.match, ...patch } } : r))
    );
  const moveRule = (index: number, delta: -1 | 1) =>
    setRules((prev) => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) {
        return prev;
      }
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  const removeRule = (index: number) => setRules((prev) => prev.filter((_, i) => i !== index));

  const persist = async (cleaned: RatingRule[]) => {
    if (isPlatform) {
      await savePlatformDefault(cleaned, description || null, highRiskJurisdictions);
    } else if (isNew) {
      await createProfile({
        name,
        slug,
        description: description || null,
        rules: cleaned,
        high_risk_jurisdictions: highRiskJurisdictions,
      });
    } else if (existing) {
      await updateProfile(existing.id, {
        name,
        description: description || null,
        rules: cleaned,
        high_risk_jurisdictions: highRiskJurisdictions,
      });
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await persist(rules.map(cleanRule));
      notifications.show({
        title: 'Risk profile saved',
        message: 'Reports it governs have been re-rated from their findings.',
        color: 'green',
      });
      navigate('/admin/risk-profiles');
    } catch {
      // the hook has already shown the failure
    } finally {
      setSaving(false);
    }
  };

  const resetToBuiltin = async () => {
    const builtin = await loadBuiltinDefault();
    setRules(builtin.rules);
  };

  const waitingForSource = !seeded && (isLoading || (isNamed && !existing));
  if (waitingForSource) {
    return (
      <Center h={300}>
        <Loader />
      </Center>
    );
  }

  const title = isPlatform
    ? 'Platform default'
    : isNew
      ? 'New risk profile'
      : name || 'Risk profile';
  const identityComplete = isPlatform || (name.trim() !== '' && (isNamed || slug.trim() !== ''));
  const canSave = rules.length > 0 && identityComplete;

  return (
    <Container size="xl" py="md">
      <Stack>
        <Group justify="space-between" align="flex-start">
          <PageTitle title={title} />
          <Group>
            {isPlatform ? (
              <Tooltip label="Load the shipped rules into the editor (nothing is saved until you press Save).">
                <Button
                  variant="default"
                  leftSection={<IconRestore size={16} />}
                  onClick={() => void resetToBuiltin()}
                >
                  Reset to built-in
                </Button>
              </Tooltip>
            ) : null}
            <Button variant="default" onClick={() => navigate('/admin/risk-profiles')}>
              Cancel
            </Button>
            <Button
              onClick={() => void save()}
              loading={saving}
              disabled={!canSave}
              data-testid="risk-profile-save"
            >
              Save
            </Button>
          </Group>
        </Group>

        <LogoPaper>
          <Stack gap="sm">
            {isPlatform ? (
              <Alert variant="light">
                These rules judge every report whose config has no profile of its own. Saving
                re-rates all of them from their findings ledger.
              </Alert>
            ) : (
              <Group grow align="flex-start">
                <TextInput
                  label="Name"
                  required
                  value={name}
                  onChange={(e) => {
                    const value = e.currentTarget.value;
                    setName(value);
                    if (isNew && !slugTouched) {
                      setSlug(slugify(value));
                    }
                  }}
                  data-testid="risk-profile-name"
                />
                <TextInput
                  label="Slug"
                  description={isNew ? 'URL-safe identifier; fixed after creation.' : 'Fixed.'}
                  required
                  disabled={!isNew}
                  value={slug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    setSlug(e.currentTarget.value);
                  }}
                  data-testid="risk-profile-slug"
                />
              </Group>
            )}
            <Textarea
              label="Description"
              autosize
              minRows={1}
              value={description}
              onChange={(e) => setDescription(e.currentTarget.value)}
            />
            <MultiSelect
              label="High-risk jurisdictions"
              description="Rules reference this list through their High-risk subject column, and subjects whose client-supplied jurisdictions overlap it carry the HRJ flag — even when their rating is Clear."
              placeholder={
                highRiskJurisdictions.length === 0 ? 'None — jurisdiction-neutral' : undefined
              }
              data={jurisdictions.map((j) => ({
                value: j.alpha2code,
                label: `${j.name} (${j.alpha2code})`,
              }))}
              value={highRiskJurisdictions}
              onChange={setHighRiskJurisdictions}
              searchable
              clearable
              data-testid="risk-profile-hrj"
            />
          </Stack>
        </LogoPaper>

        <LogoPaper>
          <Stack gap="sm">
            <Group justify="space-between">
              <Stack gap={2}>
                <Text fw={600}>Rules</Text>
                <Text size="sm" c="dimmed">
                  Evaluated top to bottom; a finding takes the first rule it matches. Empty fields
                  match anything. A finding no rule matches rates Potential.
                </Text>
              </Stack>
              <Button
                variant="default"
                leftSection={<IconPlus size={16} />}
                onClick={() => setRules((prev) => [...prev, blankRule()])}
                data-testid="risk-profile-add-rule"
              >
                Add rule
              </Button>
            </Group>
            {/* Fixed layout: the sized columns keep their width and the two
                unsized ones (Categories, Label) share what is left, so no
                input is ever squeezed to a few characters. */}
            <Table.ScrollContainer minWidth={1510}>
              <Table verticalSpacing="xs" layout="fixed" striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={36} ta="center">
                      #
                    </Table.Th>
                    <Table.Th w={190}>Kind</Table.Th>
                    <Table.Th w={210}>Categories</Table.Th>
                    <Table.Th w={140}>Severity</Table.Th>
                    <Table.Th w={120}>Sanctions-related</Table.Th>
                    <Table.Th w={150}>High-risk subject</Table.Th>
                    <Table.Th w={84}>Min conf.</Table.Th>
                    <Table.Th w={140}>Rating</Table.Th>
                    <Table.Th>Label</Table.Th>
                    <Table.Th w={96} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rules.map((rule, index) => (
                    <RuleRow
                      // Rules have no identity of their own; position is the key.
                      key={index}
                      index={index}
                      rule={rule}
                      isFirst={index === 0}
                      isLast={index === rules.length - 1}
                      onChange={(patch) => updateRule(index, patch)}
                      onMatchChange={(patch) => updateMatch(index, patch)}
                      onMove={(delta) => moveRule(index, delta)}
                      onRemove={() => removeRule(index)}
                    />
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            {rules.length === 0 ? (
              <Text size="sm" c="dimmed">
                No rules yet — every retained finding would rate Potential.
              </Text>
            ) : null}
          </Stack>
        </LogoPaper>
      </Stack>
    </Container>
  );
}
